import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { PropertiesService } from '../../../core/services/properties.service';
import { PaymentPlan, Property } from '../../../core/models/property.model';
import { Project } from '../../../core/models/project.model';
import { ProjectsService } from '../../../core/services/projects.service';
import { LeadsService, LEADS_PAGE_SIZE } from '../../../core/services/leads.service';
import { SheetBrandingService } from '../../../core/services/sheet-branding.service';
import { PropertyCardComponent } from '../../../shared/components/property-card/property-card.component';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';
import { CurrencyService } from '../../../core/services/currency.service';
import { PROPERTY_TYPE_OPTIONS, PropertyTypeValue } from '../../../shared/data/property-types';
import { downloadCsv } from '../../../shared/utils/csv-export.util';
import { ExportReportMenuComponent } from '../../../shared/components/export-report-menu/export-report-menu.component';
import { ReportExportFormat, downloadPdfReport } from '../../../shared/utils/report-export.util';
import { SheetImageSource, downloadStyledXlsx } from '../../../shared/utils/styled-workbook.util';
import {
    PROPERTY_SHEET_IMAGE_BOXES,
    PropertyReportBranding,
    buildPropertyReportColumns,
    buildPropertyReportOptions,
    propertyPlanColumnHeader,
    propertyReportStatusRank,
    isHoldLeadStatus,
    sheetStatusForProperty
} from '../../../shared/utils/properties-export.util';
import { PropertyUploadService } from '../../../core/services/property-upload.service';
import { CountrySelectorComponent } from '../../../shared/components/country-selector/country-selector.component';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { hasPropertyCreatePermission, hasPropertyEditPermission, permissionSetFromRaw } from '../../../shared/utils/permissions.util';
import { propertyStatusKey } from '../../../shared/utils/property-status.util';
import { propertyFeatureLabels, propertyFeatureSearchTerms, propertyHasGarden, propertyHasTerrace } from '../../../shared/utils/property-features.util';
import { NotificationService } from '../../../services/notification.service';
import { StableStickyFilterDirective } from '../../../shared/directives/stable-sticky-filter.directive';

type SortKey = 'price-low' | 'price-high' | 'ppsqm-low' | 'ppsqm-high' | 'bedrooms' | 'bathrooms' | 'title' | 'unit-type' | 'recent';
type BulkPriceEditItem = {
    id: string;
    title: string;
    unitNumber: string | null;
    currency: string | null;
    originalPrice: number | null;
    priceInput: string;
};

@Component({
    selector: 'app-developer-properties',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, PropertyCardComponent, LucideIconsModule, LoadingSpinnerComponent, CountrySelectorComponent, ExportReportMenuComponent, StableStickyFilterDirective],
    templateUrl: './developer-properties.component.html',
    styleUrls: ['./developer-properties.component.scss'],
})
export class DeveloperPropertiesComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('filterSentinel', { static: true }) private filterSentinelRef!: ElementRef<HTMLElement>;

    private destroyRef = inject(DestroyRef);
    private propertiesService = inject(PropertiesService);
    private projectsService = inject(ProjectsService);
    private leadsService = inject(LeadsService);
    private sheetBranding = inject(SheetBrandingService);
    private uploads = inject(PropertyUploadService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private store = inject(Store);
    private notifications = inject(NotificationService);
    currencyService = inject(CurrencyService);
    private currencySub: ReturnType<typeof this.currencyService.currencyChanges$.subscribe> | null = null;

    allProperties: Property[] = [];
    filteredProperties: Property[] = [];
    projectsForFilter: Project[] = [];
    projectsLoading = false;
    projectsError: string | null = null;
    private projectsLoaded = false;
    private projectsLoadPromise: Promise<void> | null = null;
    availableTags: string[] = [];
    readonly propertyTypeOptions = PROPERTY_TYPE_OPTIONS;
    bedroomOptions = [1, 2, 3, 4, 5, 6];
    bathroomOptions = [1, 2, 3, 4, 5, 6];
    readonly propertyStatusFilterOptions = [
        { value: 'available', label: 'Available' },
        { value: 'reserved', label: 'Reserved / Hold' },
        { value: 'reservedwithdeposit', label: 'Reserved with deposit' },
        { value: 'sold', label: 'Sold' },
        { value: 'contracted', label: 'Contracted' },
        { value: 'pendingapproval', label: 'Pending Approval' },
        { value: 'unpublished', label: 'Unpublished' }
    ] as const;
    readonly bulkImportCurrencyOptions = ['EGP', 'USD', 'AED', 'SAR', 'QAR', 'EUR', 'GBP'];
    totalCount = 0;
    loading = true;
    loadingPage = false;
    currentPage = 1;
    pageSize = 10;
    hasNextPage = true;
    private initializedFromUrl = false;
    showFilters = false;
    filterTab: 'general' | 'location' = 'general';
    private tagSet = new Set<string>();
    private viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    isFilterStuck = false;
    private isFilterCollapsed = false;
    private bodyScrollLocked = false;
    stickyTopOffsetPx = 0;
    private stickyFrameId: number | null = null;
    private stickyTriggerNeedsUpdate = false;
    private stickyTriggerScrollY: number | null = null;
    private stickyTopOffsetElement: HTMLElement | null = null;

    filters: {
        search: string;
        status: string;
        sortBy: SortKey;
        country: string;
        location: string;
        projectId: string;
        propertyType: PropertyTypeValue;
        bedrooms?: number | null;
        bathrooms?: number | null;
        tags: string[];
        featureGarden: boolean;
        featureTerrace: boolean;
        featureGroundFloor: boolean;
        minPrice?: number | null;
        maxPrice?: number | null;
    } = {
        search: '',
        status: 'any',
        sortBy: 'price-low',
        country: '',
        location: '',
        projectId: '',
        propertyType: 'any',
        bedrooms: null,
        bathrooms: null,
        tags: [],
        featureGarden: false,
        featureTerrace: false,
        featureGroundFloor: false,
        minPrice: null,
        maxPrice: null
    };

    bulkImportOpen = false;
    bulkImportStep: 1 | 2 = 1;
    bulkImportForm: {
        project_id: string;
        currency_code: string;
        country_code: string;
        create_leads_for_reserved: boolean;
    } = {
        project_id: '',
        currency_code: 'EGP',
        country_code: 'EG',
        create_leads_for_reserved: true
    };
    bulkImportFile: File | null = null;
    bulkImportFileName: string | null = null;
    bulkImportUploadedFileId: string | null = null;
    bulkImportPreviewResult: any = null;
    bulkImportApplyResult: any = null;
    bulkImportPreviewing = false;
    bulkImportApplying = false;
    bulkImportError: string | null = null;
    bulkPriceEditorOpen = false;
    bulkPriceLoading = false;
    bulkPriceSubmitting = false;
    bulkPriceError: string | null = null;
    bulkPriceItems: BulkPriceEditItem[] = [];
    exporting = false;
    exportingFormat: ReportExportFormat | null = null;
    permissionCodes: string[] = [];

    get isMobileViewport(): boolean {
        return this.viewportWidth < 1024;
    }

    get isStickyCompact(): boolean {
        if (this.showFilters) return false;
        return this.isMobileViewport ? this.isFilterStuck : this.isFilterCollapsed;
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.queueStickyUpdate();
    }

    private queueStickyUpdate(remeasureTrigger = false): void {
        if (typeof window === 'undefined') return;
        this.stickyTriggerNeedsUpdate ||= remeasureTrigger;
        if (this.stickyFrameId !== null) return;
        this.stickyFrameId = window.requestAnimationFrame(() => {
            this.stickyFrameId = null;
            this.updateStickyTopOffset();
            if (this.stickyTriggerNeedsUpdate) this.updateStickyTriggerPosition();
            this.stickyTriggerNeedsUpdate = false;
            this.updateFilterStuckState();
            this.updateFilterCollapsedState();
        });
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        this.updateStickyTopOffset();
        this.queueStickyUpdate(true);
        this.syncBodyScrollLock();
    }

    ngOnInit(): void {
        this.onWindowResize();
        this.syncBodyScrollLock();
        this.currencySub = this.currencyService.currencyChanges$.subscribe(() => this.applyLocalFilters(this.allProperties));
        this.store.select(selectCurrentUser).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user: any) => {
            const raw = user?.permission_codes ?? user?.permissionCodes ?? [];
            this.permissionCodes = Array.isArray(raw) ? raw.map((value: any) => String(value || '').trim()) : [];
        });
        void this.loadProjectsForFilter();
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const changed = this.applyStateFromQueryParams(params);
            if (!this.initializedFromUrl || changed) {
                this.initializedFromUrl = true;
                this.fetchPage();
            }
        });
    }

    ngAfterViewInit(): void {
        this.queueStickyUpdate(true);
    }

    ngOnDestroy(): void {
        if (this.stickyFrameId !== null) window.cancelAnimationFrame(this.stickyFrameId);
        this.currencySub?.unsubscribe();
        this.setBodyScrollLocked(false);
    }

    get selectedBulkImportProject(): Project | null {
        const id = String(this.bulkImportForm?.project_id || '').trim();
        if (!id) return null;
        return this.projectsForFilter.find((p) => String(p?.id || '') === id) || null;
    }

    private permissionSet(): Set<string> {
        return permissionSetFromRaw(this.permissionCodes);
    }

    get canCreateProperties(): boolean {
        return hasPropertyCreatePermission(this.permissionSet());
    }

    get canBulkImportProperties(): boolean {
        const perms = this.permissionSet();
        return hasPropertyCreatePermission(perms) || hasPropertyEditPermission(perms);
    }

    get canBulkEditPrices(): boolean {
        return hasPropertyEditPermission(this.permissionSet());
    }

    openSendOffer(property: Property): void {
        const id = String(property?.id || '').trim();
        if (!id) return;
        this.router.navigate(['/developer/properties/view', id], { queryParams: { action: 'send-offer' } }).catch(() => {});
    }

    openBulkImport(): void {
        if (!this.canBulkImportProperties) return;
        this.bulkImportOpen = true;
        this.bulkImportStep = 1;
        this.bulkImportError = null;
        this.bulkImportPreviewResult = null;
        this.bulkImportApplyResult = null;
        this.bulkImportPreviewing = false;
        this.bulkImportApplying = false;
        this.bulkImportFile = null;
        this.bulkImportFileName = null;
        this.bulkImportUploadedFileId = null;

        const preferredProjectId = String(this.filters?.projectId || '').trim();
        this.bulkImportForm = {
            project_id: preferredProjectId,
            currency_code: 'EGP',
            country_code: '',
            create_leads_for_reserved: true
        };
        this.onBulkImportProjectChanged();
    }

    closeBulkImport(): void {
        if (this.bulkImportPreviewing || this.bulkImportApplying) return;
        this.bulkImportOpen = false;
    }

    async openBulkPriceEditor(): Promise<void> {
        if (!this.canBulkEditPrices || this.bulkPriceLoading || this.bulkPriceSubmitting) return;
        this.bulkPriceEditorOpen = true;
        this.bulkPriceLoading = true;
        this.bulkPriceError = null;
        this.bulkPriceItems = [];
        try {
            const { items } = await this.fetchAllPropertiesForList(this.buildApiFilters());
            const rows = this.filterLocalProperties(items);
            this.bulkPriceItems = rows
                .slice()
                .sort((a, b) => this.bulkPriceLabel(a).localeCompare(this.bulkPriceLabel(b), undefined, { numeric: true, sensitivity: 'base' }))
                .map((property) => ({
                    id: String(property?.id || '').trim(),
                    title: String(property?.title || '').trim(),
                    unitNumber: property?.unitNumber ? String(property.unitNumber).trim() : null,
                    currency: property?.currency || property?.currencyInfo?.code || null,
                    originalPrice: typeof property?.price === 'number' && Number.isFinite(property.price) ? property.price : null,
                    priceInput:
                        property?.price === null || property?.price === undefined || !Number.isFinite(property.price)
                            ? ''
                            : String(property.price)
                }));
        } catch (err: any) {
            this.bulkPriceError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load units for bulk pricing.';
        } finally {
            this.bulkPriceLoading = false;
        }
    }

    closeBulkPriceEditor(): void {
        if (this.bulkPriceSubmitting) return;
        this.bulkPriceEditorOpen = false;
        this.bulkPriceLoading = false;
        this.bulkPriceError = null;
        this.bulkPriceItems = [];
    }

    bulkPriceHasChanges(): boolean {
        return this.collectBulkPriceChanges().length > 0;
    }

    bulkPriceInputInvalid(item: BulkPriceEditItem): boolean {
        const raw = String(item?.priceInput || '').trim();
        if (!raw) return item.originalPrice !== null;
        const normalized = raw.replace(/,/g, '');
        const parsed = Number(normalized);
        return !Number.isFinite(parsed) || parsed < 0;
    }

    async saveBulkPrices(): Promise<void> {
        if (!this.canBulkEditPrices || this.bulkPriceSubmitting || this.bulkPriceLoading) return;

        const invalidItem = this.bulkPriceItems.find((item) => this.bulkPriceInputInvalid(item));
        if (invalidItem) {
            this.bulkPriceError = `Enter a valid price for ${this.bulkPriceLabel(invalidItem)}.`;
            return;
        }

        const changes = this.collectBulkPriceChanges();
        if (!changes.length) {
            this.closeBulkPriceEditor();
            return;
        }

        this.bulkPriceSubmitting = true;
        this.bulkPriceError = null;
        const failures: string[] = [];
        let successCount = 0;
        try {
            const chunkSize = 10;
            for (let index = 0; index < changes.length; index += chunkSize) {
                const chunk = changes.slice(index, index + chunkSize);
                const settled = await Promise.allSettled(
                    chunk.map((item) =>
                        firstValueFrom(this.propertiesService.updateProperty(item.id, { price: item.nextPrice }))
                    )
                );
                settled.forEach((result, chunkIndex) => {
                    if (result.status === 'fulfilled') {
                        successCount += 1;
                        return;
                    }
                    const reason: any = result.reason;
                    const message =
                        reason?.error?.error?.message
                        || reason?.error?.message
                        || reason?.message
                        || 'Failed to update price';
                    failures.push(`${this.bulkPriceLabel(chunk[chunkIndex])}: ${message}`);
                });
            }

            if (successCount > 0) {
                this.notifications
                    .showSuccess(successCount === 1 ? '1 unit price updated.' : `${successCount} unit prices updated.`)
                    .subscribe();
            }

            if (failures.length) {
                this.bulkPriceError =
                    successCount > 0
                        ? `Updated ${successCount} unit prices, but ${failures.length} failed. ${failures[0]}`
                        : failures[0];
            }

            this.currentPage = 1;
            this.fetchPage(false);

            if (!failures.length) {
                this.closeBulkPriceEditor();
            }
        } finally {
            this.bulkPriceSubmitting = false;
        }
    }

    onBulkImportProjectChanged(): void {
        const project = this.selectedBulkImportProject;
        if (!project) return;
        if (this.bulkImportForm.country_code) return;

        const candidate = String(project.countryCode || project.country || '')
            .trim()
            .toUpperCase();
        if (/^[A-Z]{2}$/.test(candidate)) {
            this.bulkImportForm.country_code = candidate;
        }
    }

    canProceedToBulkImportStep2(): boolean {
        if (!this.canBulkImportProperties) return false;
        const projectId = String(this.bulkImportForm?.project_id || '').trim();
        const currency = String(this.bulkImportForm?.currency_code || '').trim().toUpperCase();
        const country = String(this.bulkImportForm?.country_code || '').trim().toUpperCase();
        if (!projectId) return false;
        if (!/^[A-Z]{3}$/.test(currency)) return false;
        if (!/^[A-Z]{2}$/.test(country)) return false;
        return true;
    }

    goToBulkImportStep2(): void {
        if (!this.canProceedToBulkImportStep2()) return;
        this.bulkImportStep = 2;
        this.bulkImportError = null;
    }

    onBulkImportFileChange(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files && input.files.length ? input.files[0] : null;
        this.bulkImportFile = file;
        this.bulkImportFileName = file ? file.name : null;
        this.bulkImportUploadedFileId = null;
        this.bulkImportPreviewResult = null;
        this.bulkImportApplyResult = null;
        this.bulkImportError = null;
    }

    canRunBulkImportPreview(): boolean {
        return Boolean(this.bulkImportFile && this.canProceedToBulkImportStep2());
    }

    async runBulkImportPreview(): Promise<void> {
        if (!this.canBulkImportProperties) return;
        if (!this.bulkImportFile) return;
        if (!this.canProceedToBulkImportStep2()) return;
        if (this.bulkImportPreviewing || this.bulkImportApplying) return;

        this.bulkImportError = null;
        this.bulkImportPreviewResult = null;
        this.bulkImportApplyResult = null;
        this.bulkImportPreviewing = true;
        try {
            const fileId = this.bulkImportUploadedFileId
                || (await firstValueFrom(this.uploads.uploadFile(this.bulkImportFile, { owner: 'org' }))).file_id;
            this.bulkImportUploadedFileId = fileId;
            const resp = await firstValueFrom(
                this.propertiesService.bulkImportPreview(fileId, {
                    project_id: this.bulkImportForm.project_id,
                    currency_code: this.bulkImportForm.currency_code,
                    country_code: this.bulkImportForm.country_code,
                    create_leads_for_reserved: this.bulkImportForm.create_leads_for_reserved
                })
            );
            this.bulkImportPreviewResult = resp;
        } catch (err: any) {
            this.bulkImportError =
                err?.error?.error?.message
                || err?.error?.message
                || err?.message
                || 'Failed to run preview';
        } finally {
            this.bulkImportPreviewing = false;
        }
    }

    canApplyBulkImport(): boolean {
        const errors = this.bulkImportPreviewResult?.errors;
        if (!this.bulkImportFile) return false;
        if (!this.bulkImportPreviewResult) return false;
        if (Array.isArray(errors) && errors.length) return false;
        return true;
    }

    async applyBulkImport(): Promise<void> {
        if (!this.canBulkImportProperties) return;
        if (!this.bulkImportFile) return;
        if (!this.canApplyBulkImport()) return;
        if (this.bulkImportApplying || this.bulkImportPreviewing) return;

        this.bulkImportError = null;
        this.bulkImportApplying = true;
        try {
            const fileId = this.bulkImportUploadedFileId
                || (await firstValueFrom(this.uploads.uploadFile(this.bulkImportFile, { owner: 'org' }))).file_id;
            this.bulkImportUploadedFileId = fileId;
            const resp = await firstValueFrom(
                this.propertiesService.bulkImportApply(fileId, {
                    project_id: this.bulkImportForm.project_id,
                    currency_code: this.bulkImportForm.currency_code,
                    country_code: this.bulkImportForm.country_code,
                    create_leads_for_reserved: this.bulkImportForm.create_leads_for_reserved
                })
            );
            this.bulkImportApplyResult = resp;
            this.currentPage = 1;
            this.fetchPage(false);
        } catch (err: any) {
            this.bulkImportError =
                err?.error?.error?.message
                || err?.error?.message
                || err?.message
                || 'Failed to apply import';
        } finally {
            this.bulkImportApplying = false;
        }
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        if (this.showFilters) this.isFilterCollapsed = false;
        this.syncBodyScrollLock();
        this.queueStickyUpdate(true);
    }

    closeFilters(): void {
        this.showFilters = false;
        this.syncBodyScrollLock();
        this.queueStickyUpdate(true);
    }

    private syncBodyScrollLock(): void {
        this.setBodyScrollLocked(Boolean(this.showFilters && this.isMobileViewport));
    }

    private setBodyScrollLocked(locked: boolean): void {
        if (typeof document === 'undefined') return;
        if (locked === this.bodyScrollLocked) return;
        this.bodyScrollLocked = locked;
        document.body.style.overflow = locked ? 'hidden' : '';
    }

    private updateStickyTopOffset(): void {
        if (typeof document === 'undefined') {
            this.stickyTopOffsetPx = 0;
            return;
        }

        if (!this.stickyTopOffsetElement?.isConnected) {
            const selectors = ['app-public-header header', 'app-dashboard-header header', 'header.sticky.top-0'];
            for (const selector of selectors) {
                const el = document.querySelector(selector);
                if (!(el instanceof HTMLElement)) continue;
                this.stickyTopOffsetElement = el;
                break;
            }
        }

        const el = this.stickyTopOffsetElement;
        if (!(el instanceof HTMLElement)) {
            this.stickyTopOffsetPx = 0;
            return;
        }

        const rect = el.getBoundingClientRect();
        const bottom = rect?.bottom ?? 0;
        if (!Number.isFinite(bottom)) {
            this.stickyTopOffsetPx = 0;
            return;
        }

        this.stickyTopOffsetPx = Math.max(0, Math.floor(bottom));
    }

    private updateStickyTriggerPosition(): void {
        if (typeof window === 'undefined') return;
        const sentinel = this.filterSentinelRef?.nativeElement;
        if (!sentinel) return;
        const rect = sentinel.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset || 0;
        this.stickyTriggerScrollY = rect.top + scrollY;
    }

    private updateFilterStuckState(): void {
        if (typeof window === 'undefined') return;
        if (this.stickyTriggerScrollY === null) this.updateStickyTriggerPosition();
        if (this.stickyTriggerScrollY === null) return;

        const top = this.stickyTopOffsetPx;
        const enterEpsilonPx = 1;
        const exitBufferPx = 48;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const enterAt = this.stickyTriggerScrollY - top + enterEpsilonPx;
        const exitAt = this.stickyTriggerScrollY - top - exitBufferPx;

        if (!this.isFilterStuck && scrollY >= enterAt) {
            this.isFilterStuck = true;
        } else if (this.isFilterStuck && scrollY <= exitAt) {
            this.isFilterStuck = false;
        }
    }

    private updateFilterCollapsedState(): void {
        if (typeof window === 'undefined') return;
        if (this.showFilters) {
            this.isFilterCollapsed = false;
            return;
        }
        this.isFilterCollapsed = this.isFilterStuck;
    }

    toggleTag(tag: string): void {
        if (this.filters.tags.includes(tag)) {
            this.filters.tags = this.filters.tags.filter((value) => value !== tag);
        } else {
            this.filters.tags = [...this.filters.tags, tag];
        }
        this.onFiltersChange();
    }

    /**
     * "Full Report" export, shaped to the template the sales team supplied
     * (`Full Report nibni templete  - Sheet2.pdf`) — see
     * `shared/utils/properties-export.util.ts` for the measured geometry.
     *
     * The .csv keeps the same report columns plus the totals row it has always had;
     * the template has no totals row, so the .xlsx has none.
     */
    async exportProperties(format: ReportExportFormat = 'csv'): Promise<void> {
        if (this.exporting) return;
        this.exporting = true;
        this.exportingFormat = format;
        try {
            const all = await this.fetchAllPropertiesForExport();
            const rows = this.filterLocalProperties(all);
            if (!rows.length) return;

            const sortedRows = [...rows].sort((a, b) => {
                const statusDelta = propertyReportStatusRank(a?.statusBadge || a?.status)
                    - propertyReportStatusRank(b?.statusBadge || b?.status);
                if (statusDelta) return statusDelta;
                const aId = String(a?.unitNumber || a?.id || '').trim();
                const bId = String(b?.unitNumber || b?.id || '').trim();
                return aId.localeCompare(bId, undefined, { numeric: true, sensitivity: 'base' });
            });

            // The template names each payment-plan column after that plan's own term
            // ("6 YEARS"), so the header comes from the first unit that has a plan
            // in the slot.
            const planHeaders = [0, 1, 2].map((slot) => {
                const plan = sortedRows
                    .map((property) => this.paymentPlanSlot(property, slot))
                    .find((candidate) => Boolean(candidate));
                return propertyPlanColumnHeader(plan, slot);
            });
            const columns = buildPropertyReportColumns(planHeaders);
            const header = columns.map((column) => column.header);
            // Leads column is filled for every format — the old PDF/CSV path
            // passed an empty map, so the column rendered blank outside Excel.
            const leadsByProperty = await this.loadLeadNamesByProperty();
            const exportRows = sortedRows.map((property, index) =>
                this.buildPropertyReportRow(property, index + 1, leadsByProperty)
            );

            const exportDate = new Date().toISOString().slice(0, 10);
            const selectedProjectId = String(this.filters?.projectId || '').trim();
            const selectedProjectName = selectedProjectId
                ? (this.projectsForFilter.find((p) => String(p?.id || '') === selectedProjectId)?.name || null)
                : null;
            const uniqueProjects = Array.from(
                new Set(rows.map((p) => String(p?.projectName || '').trim()).filter(Boolean))
            );
            const projectLabel =
                selectedProjectName
                || (uniqueProjects.length === 1 ? uniqueProjects[0] : null)
                || 'All projects';
            const baseRaw = `nibni - Properties of ${projectLabel} - ${exportDate}`;
            const base = baseRaw
                .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '-')
                .replace(/\s+/g, ' ')
                .trim();
            if (format === 'xlsx') {
                const branding = await this.buildReportBranding(rows, projectLabel);
                await downloadStyledXlsx(
                    `${base}.xlsx`,
                    'Full Report',
                    columns,
                    exportRows,
                    buildPropertyReportOptions(exportRows, branding)
                );
            } else if (format === 'pdf') {
                await downloadPdfReport(`${base}.pdf`, `Properties of ${projectLabel}`, header, exportRows);
            } else {
                downloadCsv(`${base}.csv`, header, [...exportRows, this.exportTotalsRow(header, exportRows)]);
            }
        } catch (_err) {
            // Avoid noisy errors; export is best-effort.
        } finally {
            this.exporting = false;
            this.exportingFormat = null;
        }
    }

    private async fetchAllPropertiesForExport(): Promise<Property[]> {
        const { items } = await this.fetchAllPropertiesForList(this.buildApiFilters());
        return items;
    }

    private paymentPlanSlot(property: Property, slot: number): PaymentPlan | null {
        const plans = Array.isArray(property?.paymentPlans) ? property.paymentPlans : [];
        return plans[slot] || null;
    }

    /** Sums the headcount and each payment-plan column; .csv only. */
    private exportTotalsRow(header: readonly string[], exportRows: readonly unknown[][]): unknown[] {
        const totals: unknown[] = header.map(() => '');
        totals[1] = 'TOTAL';
        // The three payment-plan columns, by position: the plan headers
        // are project-specific, so they cannot be looked up by name.
        [7, 8, 9].forEach((colIndex) => {
            totals[colIndex] = Math.round(
                exportRows.reduce((sum, row) => {
                    const value = Number(row[colIndex]);
                    return sum + (Number.isFinite(value) ? value : 0);
                }, 0)
            );
        });
        return totals;
    }

    private buildPropertyReportRow(
        property: Property,
        rowNumber: number,
        leadsByProperty: ReadonlyMap<string, string>
    ): unknown[] {
        const numberOrNull = (value: unknown): number | null => {
            if (value === null || value === undefined || value === '') return null;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? Math.round(parsed) : null;
        };

        // Total due per payment-plan slot (1..3): down payment + all installments
        // + reservation.
        const planDueTotals = [0, 1, 2].map((slot) => {
            const plan = this.paymentPlanSlot(property, slot);
            if (!plan) return null;
            return Math.round(
                (Number(plan.installmentAmount ?? 0) * Number(plan.numberOfInstallments ?? 0))
                + Number(plan.downPayment ?? 0)
                + Number(plan.reservationAmount ?? 0)
            );
        });

        return [
            rowNumber,
            property.unitNumber || property.id,
            sheetStatusForProperty(property.statusBadge || property.status || ''),
            numberOrNull(property.bua),
            numberOrNull(property.bedrooms),
            numberOrNull(property.bathrooms),
            Math.round(Number(property.gardenArea ?? 0)),
            ...planDueTotals,
            this.exportLeadNamesForProperty(property, leadsByProperty)
        ];
    }

    private exportLeadNamesForProperty(
        property: Property,
        leadsByProperty: ReadonlyMap<string, string>
    ): string {
        const status = propertyStatusKey(property.statusBadge || property.status || '');
        if (!['reserved', 'reservedwithdeposit', 'sold', 'contracted'].includes(status)) return '';
        return leadsByProperty.get(String(property.id || '').trim()) || '';
    }
    /**
     * The template's `Leads` column: whoever is on the unit. Best-effort — a member
     * without lead visibility gets a 403 and an empty column rather than a failed
     * export.
     */
    private async loadLeadNamesByProperty(): Promise<Map<string, string>> {
        const names = new Map<string, string[]>();
        const perPage = LEADS_PAGE_SIZE;
        const maxPages = 200;

        for (let page = 1; page <= maxPages; page += 1) {
            const result = await firstValueFrom(
                this.leadsService.getLeadsPage({ page, perPage }).pipe(catchError(() => of(null)))
            );
            if (!result) break;
            (result.rows || []).forEach((lead) => {
                const propertyId = String(lead?.propertyId || '').trim();
                const name = this.titleCaseExportName(lead?.customer);
                if (!propertyId || !name || name === '—') return;
                // Only leads that HOLD the unit belong in the sheet's Leads
                // column — a pipeline "new" lead next to the holder reads as a
                // contested unit (see isHoldLeadStatus for the full rationale).
                if (!isHoldLeadStatus(lead?.status)) return;
                const bucket = names.get(propertyId) || [];
                if (!bucket.includes(name)) bucket.push(name);
                names.set(propertyId, bucket);
            });

            const done = !result.rows?.length
                || result.rows.length < perPage
                || (result.total > 0 && page * perPage >= result.total);
            if (done) break;
        }

        return new Map(Array.from(names, ([propertyId, list]) => [propertyId, list.join(', ')]));
    }

    private titleCaseExportName(value: unknown): string {
        return String(value || '')
            .trim()
            .toLocaleLowerCase()
            .replace(/(^|[\s'-])([\p{L}])/gu, (_match, boundary: string, letter: string) => `${boundary}${letter.toLocaleUpperCase()}`);
    }
    /**
     * Masthead content for the .xlsx: company name and logo, what the export covers,
     * the project's location and cover shot, and the nibni mark. Every piece is
     * best-effort — a missing image degrades to text, it never fails the export.
     */
    private async buildReportBranding(
        rows: readonly Property[],
        projectLabel: string
    ): Promise<PropertyReportBranding> {
        const settings = await this.sheetBranding.loadOrgSettings();
        // A location and a cover shot only mean something when every unit in the
        // export belongs to the same project.
        const projectRef = this.exportProjectRef(rows);
        const project = projectRef
            ? await firstValueFrom(this.projectsService.getProject(projectRef).pipe(catchError(() => of(null))))
            : null;

        const [developerLogo, projectCover, nibniLogo] = await Promise.all([
            this.sheetBranding.loadFirstImage(
                [
                    { fileId: settings?.logo_file_id, url: settings?.logo_url },
                    { fileId: project?.offerLogoFileId, url: project?.offerLogoUrl }
                ],
                PROPERTY_SHEET_IMAGE_BOXES.developerLogo
            ),
            this.sheetBranding.loadFirstImage(
                [
                    { fileId: project?.coverFileId, url: project?.coverUrl },
                    { fileId: project?.offerCoverFileId, url: project?.offerCoverUrl },
                    { url: project?.image || project?.images?.[0] }
                ],
                PROPERTY_SHEET_IMAGE_BOXES.projectCover
            ),
            this.sheetBranding.loadNibniLogo(PROPERTY_SHEET_IMAGE_BOXES.nibniLogo)
        ]);

        return {
            orgName: settings?.org_name || '',
            projectLabel,
            location: project?.locationLabel || project?.locationAddress || project?.location || '',
            exportDate: new Date(),
            developerLogo,
            projectCover,
            nibniLogo
        };
    }

    /** The one project every exported unit belongs to, or null when they differ. */
    private exportProjectRef(rows: readonly Property[]): string | null {
        const projectIds = new Set(
            rows.map((property) => String(property?.project_id || '').trim()).filter(Boolean)
        );
        return projectIds.size === 1 ? Array.from(projectIds)[0] : null;
    }

    onFiltersChange() {
        this.currentPage = 1;
        this.syncUrlFromState();
        this.fetchPage();
    }

    private filterLocalProperties(source: Property[]): Property[] {
        const { search, status, sortBy, country, minPrice, maxPrice, location, propertyType, bedrooms, bathrooms, tags, featureGarden, featureTerrace, featureGroundFloor } = this.filters;
        const searchTerm = (search || '').toLowerCase();
        const locationTerm = (location || '').toLowerCase();
        return source
            .filter((p) => {
                const matchesSearch = searchTerm
                    ? [
                        p.title,
                        p.tagLine,
                        p.description,
                        p.projectName,
                        p.phaseName,
                        p.location,
                        p.country,
                        p.type,
                        p.unitNumber,
                        ...(p.tags || []),
                        ...propertyFeatureSearchTerms(p)
                    ].some((value) => (value || '').toString().toLowerCase().includes(searchTerm))
                    : true;

                const matchesStatus = this.matchesDeveloperStatusFilter(status, p);
                const matchesCountry = !country
                    ? true
                    : ((p.countryCode || p.country || '').toString().toUpperCase()) === country.toUpperCase();

                const priceValue = this.convertPrice(p) ?? 0;
                const matchesMin = minPrice ? priceValue >= minPrice : true;
                const matchesMax = maxPrice ? priceValue <= maxPrice : true;
                const matchesBedrooms = bedrooms ? (p.bedrooms || 0) >= bedrooms : true;
                const matchesBathrooms = bathrooms ? (p.bathrooms || 0) >= bathrooms : true;

                const matchesLocation = locationTerm
                    ? [
                        p.location,
                        p.projectName,
                        p.phaseName,
                        p.country,
                        p.countryName
                    ].some((value) => (value || '').toString().toLowerCase().includes(locationTerm))
                    : true;

                const matchesUnitType = propertyType === 'any'
                    ? true
                    : (p.type || '').toString().toLowerCase() === propertyType.toLowerCase();

                const matchesGarden = featureGarden ? propertyHasGarden(p) : true;
                const matchesTerrace = featureTerrace ? propertyHasTerrace(p) : true;
                const matchesGroundFloor = featureGroundFloor ? this.isGroundFloor(p.floor) : true;
                const matchesTags = tags.length === 0
                    ? true
                    : propertyFeatureLabels(p).some((label) => tags.some((tag) => tag.toLowerCase() === label.toLowerCase()));

                return matchesSearch && matchesStatus && matchesCountry && matchesMin && matchesMax && matchesBedrooms && matchesBathrooms && matchesLocation && matchesUnitType && matchesGarden && matchesTerrace && matchesGroundFloor && matchesTags;
            })
            .sort((a, b) => {
                switch (sortBy) {
                    case 'price-low':
                        return (this.convertPrice(a) || Infinity) - (this.convertPrice(b) || Infinity);
                    case 'price-high':
                        return (this.convertPrice(b) || -Infinity) - (this.convertPrice(a) || -Infinity);
                    case 'ppsqm-low':
                        return (this.pricePerSqm(a) || Infinity) - (this.pricePerSqm(b) || Infinity);
                    case 'ppsqm-high':
                        return (this.pricePerSqm(b) || -Infinity) - (this.pricePerSqm(a) || -Infinity);
                    case 'bedrooms':
                        return (b.bedrooms || 0) - (a.bedrooms || 0);
                    case 'bathrooms':
                        return (b.bathrooms || 0) - (a.bathrooms || 0);
                    case 'unit-type':
                        return (a.type || '').toString().localeCompare((b.type || '').toString());
                    case 'title':
                        return a.title.localeCompare(b.title);
                    case 'recent':
                    default:
                        return this.parseDate(b.updated_at || b.created_at) - this.parseDate(a.updated_at || a.created_at);
                }
            });
    }

    private applyLocalFilters(source: Property[]) {
        this.addFilterOptions(source);
        this.filteredProperties = this.filterLocalProperties(source);
    }

    showMore() {
        if (!this.hasNextPage || this.loadingPage) return;
        this.currentPage += 1;
        this.fetchPage(true);
    }

    private fetchPage(append = false) {
        if (this.currentPage === 1) {
            this.loading = true;
            append = false;
        }
        this.loadingPage = true;
        const apiFilters = this.buildApiFilters();
        if (this.usesClientOnlyFilters()) {
            void this.fetchAllPagesForClientFilters(apiFilters);
            return;
        }
        this.propertiesService.getPropertiesAuth(apiFilters, { page: this.currentPage, perPage: this.pageSize }).subscribe({
            next: ({ items, total, page, perPage }) => {
                const incoming = items.filter(p => !p.isDeleted);
                let nextProperties: Property[];
                if (append) {
                    const seen = new Set(this.allProperties.map((p) => p.id));
                    nextProperties = [...this.allProperties, ...incoming.filter((p) => !seen.has(p.id))];
                } else {
                    const byId = new Map<string, Property>();
                    incoming.forEach((p) => {
                        const id = String(p.id || '').trim();
                        if (id && !byId.has(id)) byId.set(id, p);
                    });
                    nextProperties = Array.from(byId.values());
                }
                const safeTotal = Number.isFinite(total) ? total : (page - 1) * perPage + items.length;
                const serverHasNext = safeTotal > page * perPage;
                const hasNextPage = serverHasNext || (items.length >= perPage && nextProperties.length < safeTotal);
                this.allProperties = nextProperties;
                this.hasNextPage = hasNextPage;
                this.totalCount = this.normalizeVisibleTotalCount(safeTotal, nextProperties.length, hasNextPage);
                this.loading = false;
                this.loadingPage = false;
                this.applyLocalFilters(this.allProperties);
            },
            error: () => {
                if (!append) {
                    this.allProperties = [];
                    this.filteredProperties = [];
                    this.totalCount = 0;
                    this.hasNextPage = false;
                } else if (this.currentPage > 1) {
                    this.currentPage -= 1;
                }
                this.loading = false;
                this.loadingPage = false;
            }
        });
    }

    private async fetchAllPagesForClientFilters(apiFilters: ReturnType<DeveloperPropertiesComponent['buildApiFilters']>): Promise<void> {
        try {
            const { items, total } = await this.fetchAllPropertiesForList(apiFilters);
            this.allProperties = items;
            this.hasNextPage = false;
            this.totalCount = this.normalizeVisibleTotalCount(total, items.length, false);
            this.applyLocalFilters(this.allProperties);
            this.loading = false;
            this.loadingPage = false;
        } catch {
            this.allProperties = [];
            this.filteredProperties = [];
            this.totalCount = 0;
            this.hasNextPage = false;
            this.loading = false;
            this.loadingPage = false;
        }
    }

    private async fetchAllPropertiesForList(apiFilters: ReturnType<DeveloperPropertiesComponent['buildApiFilters']>): Promise<{ items: Property[]; total: number }> {
        const perPage = 200;
        const maxPages = 100;
        const byId = new Map<string, Property>();
        let total = 0;
        for (let page = 1; page <= maxPages; page += 1) {
            const res = await firstValueFrom(this.propertiesService.getPropertiesAuth(apiFilters, { page, perPage }));
            const items = (res?.items || []).filter((p) => !p.isDeleted);
            total = Number(res?.total ?? total ?? items.length);
            items.forEach((p) => byId.set(String(p.id || '').trim(), p));
            if (!items.length) break;
            if (Number.isFinite(total) && total > 0 && byId.size >= total) break;
            if (items.length < perPage) break;
        }
        const uniqueItems = Array.from(byId.values());
        return {
            items: uniqueItems,
            total: this.normalizeVisibleTotalCount(total, uniqueItems.length, false)
        };
    }

    usesClientOnlyFilters(): boolean {
        return Boolean(
            (this.filters.minPrice ?? null) !== null
            || (this.filters.maxPrice ?? null) !== null
            || this.filters.featureGarden
            || this.filters.featureTerrace
            || this.filters.featureGroundFloor
            || this.filters.tags.length
        );
    }

    resultsSummary(): string {
        if (this.usesClientOnlyFilters()) {
            return `Showing ${this.filteredProperties.length} of ${this.allProperties.length} properties`;
        }
        const visibleTotal = this.hasNextPage
            ? Math.max(this.totalCount, this.allProperties.length, this.filteredProperties.length)
            : Math.max(this.allProperties.length, this.filteredProperties.length);
        return `Showing ${this.filteredProperties.length} of ${visibleTotal} properties`;
    }

    private normalizeVisibleTotalCount(reportedTotal: number, loadedCount: number, hasNextPage: boolean): number {
        const safeLoaded = Math.max(0, Number(loadedCount) || 0);
        const safeReported = Number.isFinite(reportedTotal) ? Math.max(0, Number(reportedTotal)) : safeLoaded;
        if (hasNextPage) return Math.max(safeReported, safeLoaded);
        return safeLoaded || safeReported;
    }

    private buildApiFilters() {
        const filter: any = {};
        const normalizedStatus = this.normalizeStatusFilterValue(this.filters.status);
        if (normalizedStatus && normalizedStatus !== 'any') filter.status = normalizedStatus;
        if (this.filters.search) filter.search = this.filters.search;
        if (this.filters.location) filter.location = this.filters.location;
        if (this.filters.projectId) filter.project_id = String(this.filters.projectId || '').trim();
        if (this.filters.country && !filter.project_id) filter.country = this.filters.country;
        if (this.filters.propertyType && this.filters.propertyType !== 'any') filter.type = this.filters.propertyType;
        if (this.filters.bedrooms) filter.bedrooms = this.filters.bedrooms;
        if (this.filters.bathrooms) filter.bathrooms = this.filters.bathrooms;
        return filter;
    }

    private applyStateFromQueryParams(params: import('@angular/router').ParamMap): boolean {
        const nextSearch = (params.get('search') || '').trim();
        const nextStatus = (params.get('status') || 'any').trim() || 'any';
        const nextSort = (params.get('sort') || this.filters.sortBy).trim() as SortKey;
        const nextCountry = (params.get('country') || '').trim().toUpperCase();
        const nextLocation = (params.get('location') || '').trim();
        const nextProjectId = (params.get('project_id') || '').trim();
        const nextType = (params.get('type') || 'any').trim();
        const nextBedrooms = this.parsePositiveIntOrNull(params.get('bedrooms'));
        const nextBathrooms = this.parsePositiveIntOrNull(params.get('bathrooms'));
        const nextMin = this.parseNumber(params.get('min_price'));
        const nextMax = this.parseNumber(params.get('max_price'));
        const nextGarden = this.parseBoolean(params.get('garden'));
        const nextTerrace = this.parseBoolean(params.get('terrace'));
        const nextGround = this.parseBoolean(params.get('ground_floor'));

        const sortAllowed: SortKey[] = [
            'price-low',
            'price-high',
            'ppsqm-low',
            'ppsqm-high',
            'bedrooms',
            'bathrooms',
            'title',
            'unit-type',
            'recent'
        ];
        const safeSort: SortKey = sortAllowed.includes(nextSort) ? nextSort : 'price-low';

        const typeAllowed = new Set(this.propertyTypeOptions.map((o) => o.value));
        const safeType: PropertyTypeValue = (typeAllowed.has(nextType as any) ? nextType : 'any') as PropertyTypeValue;

        let changed = false;

        this.currentPage = 1;

        if (this.filters.search !== nextSearch) {
            this.filters.search = nextSearch;
            changed = true;
        }
        if (this.filters.status !== nextStatus) {
            this.filters.status = nextStatus;
            changed = true;
        }
        if (this.filters.sortBy !== safeSort) {
            this.filters.sortBy = safeSort;
            changed = true;
        }
        if (this.filters.country !== nextCountry) {
            this.filters.country = nextCountry;
            changed = true;
        }
        if (this.filters.location !== nextLocation) {
            this.filters.location = nextLocation;
            changed = true;
        }
        if (this.filters.projectId !== nextProjectId) {
            this.filters.projectId = nextProjectId;
            changed = true;
        }
        if (this.filters.propertyType !== safeType) {
            this.filters.propertyType = safeType;
            changed = true;
        }
        if ((this.filters.bedrooms ?? null) !== nextBedrooms) {
            this.filters.bedrooms = nextBedrooms;
            changed = true;
        }
        if ((this.filters.bathrooms ?? null) !== nextBathrooms) {
            this.filters.bathrooms = nextBathrooms;
            changed = true;
        }
        if ((this.filters.minPrice ?? null) !== nextMin) {
            this.filters.minPrice = nextMin;
            changed = true;
        }
        if ((this.filters.maxPrice ?? null) !== nextMax) {
            this.filters.maxPrice = nextMax;
            changed = true;
        }
        if (this.filters.featureGarden !== nextGarden) {
            this.filters.featureGarden = nextGarden;
            changed = true;
        }
        if (this.filters.featureTerrace !== nextTerrace) {
            this.filters.featureTerrace = nextTerrace;
            changed = true;
        }
        if (this.filters.featureGroundFloor !== nextGround) {
            this.filters.featureGroundFloor = nextGround;
            changed = true;
        }

        if (nextLocation || nextProjectId) {
            this.filterTab = 'location';
        }

        return changed;
    }

    private syncUrlFromState(): void {
        const queryParams: Record<string, any> = {
            search: this.filters.search?.trim() ? this.filters.search.trim() : null,
            status: this.filters.status !== 'any' ? this.filters.status : null,
            sort: this.filters.sortBy !== 'price-low' ? this.filters.sortBy : null,
            country: this.filters.country?.trim() ? this.filters.country.trim() : null,
            location: this.filters.location?.trim() ? this.filters.location.trim() : null,
            project_id: this.filters.projectId ? String(this.filters.projectId).trim() : null,
            type: this.filters.propertyType !== 'any' ? this.filters.propertyType : null,
            bedrooms: this.filters.bedrooms !== null && this.filters.bedrooms !== undefined ? this.filters.bedrooms : null,
            bathrooms: this.filters.bathrooms !== null && this.filters.bathrooms !== undefined ? this.filters.bathrooms : null,
            min_price: this.filters.minPrice !== null && this.filters.minPrice !== undefined ? this.filters.minPrice : null,
            max_price: this.filters.maxPrice !== null && this.filters.maxPrice !== undefined ? this.filters.maxPrice : null,
            garden: this.filters.featureGarden ? 1 : null,
            terrace: this.filters.featureTerrace ? 1 : null,
            ground_floor: this.filters.featureGroundFloor ? 1 : null
        };
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            replaceUrl: true
        });
    }

    private parsePositiveInt(raw: string | null, fallback: number): number {
        const n = raw ? Number.parseInt(raw, 10) : NaN;
        return Number.isFinite(n) && n > 0 ? n : fallback;
    }

    private parsePositiveIntOrNull(raw: string | null): number | null {
        const n = raw ? Number.parseInt(raw, 10) : NaN;
        return Number.isFinite(n) && n > 0 ? n : null;
    }

    private parseNumber(raw: string | null): number | null {
        if (!raw || !raw.trim()) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }

    private parseBoolean(raw: string | null): boolean {
        if (!raw || !raw.trim()) return false;
        const v = raw.trim().toLowerCase();
        return ['1', 'true', 'yes', 'on'].includes(v);
    }

    private async loadProjectsForFilter(): Promise<void> {
        if (this.projectsLoaded) return;
        if (this.projectsLoadPromise) return this.projectsLoadPromise;

        this.projectsLoading = true;
        this.projectsError = null;

        this.projectsLoadPromise = (async () => {
            const perPage = 50;
            let page = 1;
            let total = 0;
            const all: Project[] = [];

            const maxPages = 10;
            for (let i = 0; i < maxPages; i++) {
                const resp = await firstValueFrom(this.projectsService.getProjectsAuth({ page, perPage }));
                const items = Array.isArray(resp?.items) ? resp.items : [];
                total = Number(resp?.total ?? items.length);
                all.push(...items);
                if (!items.length) break;
                if (Number.isFinite(total) && total > 0 && all.length >= total) break;
                if (items.length < perPage) break;
                page += 1;
            }

            const seen = new Set<string>();
            const unique = all
                .filter((p) => {
                    const id = String(p?.id || '').trim();
                    if (!id || seen.has(id)) return false;
                    seen.add(id);
                    return true;
                })
                .filter((p) => !p?.isDeleted);

            unique.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

            this.projectsForFilter = unique;
            this.projectsLoaded = true;
        })()
            .catch((err) => {
                this.projectsError = err?.message || 'Failed to load projects';
            })
            .finally(() => {
                this.projectsLoading = false;
                this.projectsLoadPromise = null;
            });

        return this.projectsLoadPromise;
    }

    private parseDate(value?: string | null): number {
        if (!value) return 0;
        const normalized = value.replace(' ', 'T').replace(/:(\d{3})$/, '.$1Z');
        const ts = Date.parse(normalized);
        return Number.isFinite(ts) ? ts : 0;
    }

    private convertPrice(property: Property): number | null {
        if (property?.price === null || property?.price === undefined) return null;
        const fromCode = property.currency || property.currencyInfo?.code || 'USD';
        return this.currencyService.convert(property.price, fromCode);
    }

    private addFilterOptions(items: Property[]): void {
        this.tagSet.clear();
        for (const property of items) {
            propertyFeatureLabels(property).forEach((tag) => {
                if (tag) this.tagSet.add(tag);
            });
        }
        this.availableTags = Array.from(this.tagSet).sort();
    }

    private normalizeStatusFilterValue(value: unknown): string {
        const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
        if (!normalized) return 'any';
        if (normalized === 'available') return 'available';
        if (['reserved', 'reserve', 'hold', 'held', 'reservedhold', 'reservedorhold', 'reservedwithdeposit'].includes(normalized)) return 'reserved';
        if (['sold', 'contracted', 'contract'].includes(normalized)) return 'sold';
        if (['draft', 'pendingapproval', 'unpublished'].includes(normalized)) return normalized;
        return 'any';
    }

    private matchesDeveloperStatusFilter(status: string, property: Property): boolean {
        const normalized = this.normalizeStatusFilterValue(status);
        if (normalized === 'any') return true;
        const badge = propertyStatusKey(property.statusBadge || property.status || '');
        if (normalized === 'reserved') return badge === 'reserved' || badge === 'reservedwithdeposit';
        if (normalized === 'sold') return badge === 'sold' || badge === 'contracted';
        return badge === normalized;
    }

    private pricePerSqm(p: Property): number | null {
        const price = this.convertPrice(p);
        const area = p.bua || p.totalArea || p.area || null;
        if (!price || !area || area <= 0) return null;
        return price / area;
    }

    private isGroundFloor(floor: string | null | undefined): boolean {
        if (!floor) return false;
        const v = String(floor).trim().toLowerCase();
        if (v === '0' || v === '0.0') return true;
        if (v.includes('ground')) return true;
        if (v === 'gf' || v.startsWith('gf ')) return true;
        return false;
    }

    private bulkPriceLabel(item: Pick<BulkPriceEditItem, 'title' | 'unitNumber'> | Property): string {
        const title = String(item?.title || '').trim();
        const unit = 'unitNumber' in item && item.unitNumber ? String(item.unitNumber).trim() : '';
        if (title && unit) return `${title} · Unit ${unit}`;
        if (title) return title;
        if (unit) return `Unit ${unit}`;
        return 'Untitled unit';
    }

    private collectBulkPriceChanges(): Array<BulkPriceEditItem & { nextPrice: number }> {
        return this.bulkPriceItems.flatMap((item) => {
            const raw = String(item?.priceInput || '').trim();
            if (!raw) return [];
            const nextPrice = Number(raw.replace(/,/g, ''));
            if (!Number.isFinite(nextPrice) || nextPrice < 0) return [];
            const original = item.originalPrice;
            if (original !== null && Math.abs(nextPrice - original) < 0.000001) return [];
            return [{ ...item, nextPrice }];
        });
    }

    private rebuildUnitTypes(_items: Property[]): void {
        // Deprecated: property type options are now fixed, not data-derived.
    }
}
